import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { CareCreditIntegrationCustom, CareCreditIntegrationV1 } from '@frontend/api-care-credit-integration';
import { MultiStepControl } from '@frontend/design-system';
import { useCareCreditEnrollmentConfirmApplication } from '../../../hooks/use-care-credit-enrollment-confirm-application';
import { useCareCreditEnrollmentSubmitApplication } from '../../../hooks/use-care-credit-enrollment-submit-application';
import { useCareCreditLocationProviderStatus } from '../../../hooks/use-care-credit-location-provider-status';
import {
  CareCreditEnrollmentMultiStepBaseProps,
  EnrollmentConfirmationData,
  EnrollmentFormState,
  EnrollmentFormFieldNames,
  EnrollmentApplicationResponse,
  CareCreditEnrollmentMultiSteps,
  ConfirmEnrollmentApplicationResponse,
  ConfirmEnrollmentProps,
} from '../enrollment-multi-step.types';
import { getParentStepConfig } from '../utils/get-enrollment-steps';
import {
  getPrequalificationOfferAcceptanceResponseErrorDetails,
  getPrequalificationOfferStatusResponseErrorDetails,
  getPrequalificationOfferStatusResponseStatus,
  isDeclinedPrequalificationOfferAcceptanceResponse,
} from '../utils/offer-status';
import { CareCreditEnrollmentContext } from './care-credit-enrollment.context';

export type CareCreditEnrollmentProviderProps = React.PropsWithChildren<
  Omit<CareCreditEnrollmentMultiStepBaseProps, 'multiStepProps'> & {
    multiStepControl: MultiStepControl;
  }
>;

export const CareCreditEnrollmentProvider = ({
  children,
  person,
  invoice,
  locationId,
  careCreditData,
  multiStepControl,
  onCancel,
  onGoBack,
  onGoToMessageStep,
  onEnrollmentAccepted,
  onEnrollmentDeclined,
  onSetParentStepData,
}: CareCreditEnrollmentProviderProps) => {
  const [enrollmentFormData, setEnrollmentFormData] = useState<EnrollmentFormState>({
    isValid: false,
    fields: {
      amount: '',
      firstName: '',
      middleName: '',
      lastName: '',
      suffix: '',
      dateOfBirth: '',
      email: '',
      ssn: '',
      phoneNumber: '',
      phoneNumberType: '',
      address: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      housingStatus: '',
      monthlyNetIncome: '',
      idType: '',
      idNumber: '',
      idExpirationDate: '',
      idState: '',
      idCountry: '',
      idMilitaryBranch: '',
      agreeToTerms: false,
    },
  });

  const [confirmationStepData, setConfirmationStepData] = useState<EnrollmentConfirmationData>({
    agreeToTerms: false,
    applicationRevisionDate: dayjs().format('MM/DD/YYYY'),
  });

  const [applicationResult, setApplicationResult] = useState<EnrollmentApplicationResponse | undefined>();

  const [applicationConfirmationResult, setApplicationConfirmationResult] = useState<
    ConfirmEnrollmentApplicationResponse | undefined
  >();

  const { merchantNumber } = useCareCreditLocationProviderStatus({ locationId });

  const { submitApplication, isSubmitting: isSubmittingApplication } = useCareCreditEnrollmentSubmitApplication({
    merchantNumber,
  });
  const { confirmApplication, isSubmitting: isSubmittingConfirmation } = useCareCreditEnrollmentConfirmApplication({
    merchantNumber,
  });

  const isSubmitting = isSubmittingApplication || isSubmittingConfirmation;

  const isLoading = useMemo(() => {
    return !person || !careCreditData;
  }, [person, careCreditData]);

  const onSubmitApplication = useCallback(
    async (data: EnrollmentFormState) => {
      const result = await submitApplication(data.fields);

      setApplicationResult(result);

      return result;
    },
    [submitApplication]
  );

  const getApplication = CareCreditIntegrationV1.Queries.useFetchGetPrequalificationOfferStatus();

  const onConfirmApplication = useCallback(
    async (props: ConfirmEnrollmentProps) => {
      const acceptPayload = await confirmApplication(props);

      if (isDeclinedPrequalificationOfferAcceptanceResponse(acceptPayload)) {
        const toSet: ConfirmEnrollmentApplicationResponse = {
          status: 'declined',
          message: getPrequalificationOfferAcceptanceResponseErrorDetails(acceptPayload).message,
          acceptPayload: acceptPayload,
          statusPayload: null,
        };

        setApplicationConfirmationResult(toSet);
        return toSet;
      }

      const statusPayload = await getApplication({
        request: {
          applicationId: props.enrollmentResult?.applicationId ?? '',
        },
      });

      const toSet: ConfirmEnrollmentApplicationResponse = {
        status: getPrequalificationOfferStatusResponseStatus(statusPayload),
        message: getPrequalificationOfferStatusResponseErrorDetails(statusPayload).message,
        acceptPayload,
        statusPayload,
      };

      setApplicationConfirmationResult(toSet);

      return toSet;
    },
    [confirmApplication]
  );

  const onChangeEnrollmentFormField = useCallback((field: EnrollmentFormFieldNames, value: string) => {
    setEnrollmentFormData((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [field]: value,
      },
    }));
  }, []);

  const onChangeEnrollmentFormComplete = useCallback((isComplete: boolean) => {
    setEnrollmentFormData((prev) => ({
      ...prev,
      isValid: isComplete,
    }));
  }, []);

  const onClearIdFields = useCallback(() => {
    setEnrollmentFormData((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        idNumber: '',
        idState: '',
        idCountry: '',
        idMilitaryBranch: '',
      },
    }));
  }, []);

  const onGoToCareCreditCollect = useCallback(() => {
    if (!applicationConfirmationResult) {
      console.warn('Attempting to go to CareCredit collect step without application confirmation result');
      return;
    }
    onEnrollmentAccepted(applicationConfirmationResult);
  }, [onEnrollmentAccepted, applicationConfirmationResult]);

  useEffect(() => {
    if (invoice) {
      setEnrollmentFormData((prev) => {
        const currentAmount = prev.fields.amount;

        if (currentAmount) {
          return prev;
        }

        const invoiceAmount = String(invoice ? Math.ceil(invoice.billedAmount / 100) : 0);
        return {
          ...prev,
          fields: {
            ...prev.fields,
            amount: invoiceAmount,
          },
        };
      });
    }
  }, [invoice]);

  useEffect(() => {
    const fields = CareCreditIntegrationCustom.getPatientQueryFields(person);

    if (fields) {
      setEnrollmentFormData((prev) => {
        const currentFields = {
          firstName: prev.fields.firstName,
          lastName: prev.fields.lastName,
          dateOfBirth: prev.fields.dateOfBirth,
          email: prev.fields.email,
          phoneNumber: prev.fields.phoneNumber,
          phoneNumberType: prev.fields.phoneNumberType,
          address: prev.fields.address,
          city: prev.fields.city,
          state: prev.fields.state,
          postalCode: prev.fields.postalCode,
        };

        const toSetFields = {
          firstName: prev.fields.firstName || fields.firstName,
          lastName: prev.fields.lastName || fields.lastName,
          dateOfBirth:
            prev.fields.dateOfBirth || (fields.dateOfBirth ? dayjs(fields.dateOfBirth).format('MM/DD/YYYY') : ''),
          email: prev.fields.email || fields.email,
          phoneNumber: prev.fields.phoneNumber || fields.contactNumber,
          phoneNumberType:
            prev.fields.phoneNumberType ||
            (fields.contactType
              ? CareCreditIntegrationCustom.convert.contactTypeToPersonPhone(fields.contactType)
              : fields.contactNumber) ||
            fields.contactType,
          address: prev.fields.address || fields.addressLine1,
          city: prev.fields.city || fields.city,
          state: prev.fields.state || fields.state,
          postalCode: prev.fields.postalCode || fields.zipCode,
        };

        if (isEqual(currentFields, toSetFields)) {
          return prev;
        }

        return {
          ...prev,
          fields: {
            ...prev.fields,
            ...toSetFields,
          },
        };
      });
    }
  }, [person]);

  useEffect(() => {
    /**
     * Update parent setp to hide/show header based on active sub step.
     */
    onSetParentStepData?.(getParentStepConfig(multiStepControl.activeStep as CareCreditEnrollmentMultiSteps));
  }, [multiStepControl.activeStep]);

  return (
    <CareCreditEnrollmentContext.Provider
      value={{
        // forwarded props
        locationId,
        person,
        invoice,
        careCreditData,
        multiStep: multiStepControl,
        onCancel,
        onGoBack,
        onGoToMessageStep,

        // internal state
        isLoading,
        isSubmitting,
        enrollmentFormData,
        confirmationStepData,
        applicationResult,
        applicationConfirmationResult,
        setEnrollmentFormData,
        onChangeEnrollmentFormField,
        onChangeEnrollmentFormComplete,
        setConfirmationStepData,
        onSubmitApplication,
        onConfirmApplication,
        onClearIdFields,
        onGoToCareCreditCollect,
        onEnrollmentDeclined,
      }}
    >
      {children}
    </CareCreditEnrollmentContext.Provider>
  );
};
